import React from "react";

import type { SxProps, Theme } from "@mui/material";

import AddCircleRaw from "@dashboard/assets/images/icons/streamline/add-circle.svg?raw";
import AddRaw from "@dashboard/assets/images/icons/streamline/add.svg?raw";
import DangerRaw from "@dashboard/assets/images/icons/streamline/alert-circle-solid.svg?raw";
import AlertCircleRaw from "@dashboard/assets/images/icons/streamline/alert-circle.svg?raw";
import AnalyticsRaw from "@dashboard/assets/images/icons/streamline/analytics-bars.svg?raw";
import AngleBracketsRaw from "@dashboard/assets/images/icons/streamline/angle-brackets.svg?raw";
import DropdownRaw from "@dashboard/assets/images/icons/streamline/arrow-circle-down-tailless.svg?raw";
import ChevronDownRaw from "@dashboard/assets/images/icons/streamline/arrow-down-1.svg?raw";
import ChevronLeftRaw from "@dashboard/assets/images/icons/streamline/arrow-left-1.svg?raw";
import BackRaw from "@dashboard/assets/images/icons/streamline/arrow-left.svg?raw";
import ChevronRightRaw from "@dashboard/assets/images/icons/streamline/arrow-right-1.svg?raw";
import ArrowRightRaw from "@dashboard/assets/images/icons/streamline/arrow-right.svg?raw";
import RadioCheckedRaw from "@dashboard/assets/images/icons/streamline/button-record-1.svg?raw";
import CalendarRaw from "@dashboard/assets/images/icons/streamline/calendar-date.svg?raw";
import CameraRaw from "@dashboard/assets/images/icons/streamline/camera-1.svg?raw";
import CheckRaw from "@dashboard/assets/images/icons/streamline/check-1.svg?raw";
import VerifiedRaw from "@dashboard/assets/images/icons/streamline/check-badge-solid.svg?raw";
import ApprovedRaw from "@dashboard/assets/images/icons/streamline/check-circle-1-solid.svg?raw";
import EyeOffRaw from "@dashboard/assets/images/icons/streamline/close-eyes.svg?raw";
import CloseRaw from "@dashboard/assets/images/icons/streamline/close.svg?raw";
import RadioRaw from "@dashboard/assets/images/icons/streamline/coding-apps-website-web-form-radio-button-2.svg?raw";
import PaletteRaw from "@dashboard/assets/images/icons/streamline/color-palette-sample-1.svg?raw";
import ImageFileRaw from "@dashboard/assets/images/icons/streamline/common-file-horizontal-image.svg?raw";
import CreditCardRaw from "@dashboard/assets/images/icons/streamline/credit-card-1-alternate.svg?raw";
import ReturnRaw from "@dashboard/assets/images/icons/streamline/diagram-up-then-down.svg?raw";
import DownloadRaw from "@dashboard/assets/images/icons/streamline/download-bottom.svg?raw";
import DuplicateRaw from "@dashboard/assets/images/icons/streamline/duplicate.svg?raw";
import EmailRaw from "@dashboard/assets/images/icons/streamline/email-action-unread.svg?raw";
import FilterRaw from "@dashboard/assets/images/icons/streamline/filter-sort-lines-descending.svg?raw";
import FlashRaw from "@dashboard/assets/images/icons/streamline/flash-1.svg?raw";
import FlashOffRaw from "@dashboard/assets/images/icons/streamline/flash-off.svg?raw";
import FolderRaw from "@dashboard/assets/images/icons/streamline/folder.svg?raw";
import GoogleRaw from "@dashboard/assets/images/icons/streamline/google-logo.svg?raw";
import LinkRaw from "@dashboard/assets/images/icons/streamline/hyperlink-3.svg?raw";
import InfoCircleRaw from "@dashboard/assets/images/icons/streamline/information-circle-solid.svg?raw";
import UnlinkRaw from "@dashboard/assets/images/icons/streamline/link-broken.svg?raw";
import LockRaw from "@dashboard/assets/images/icons/streamline/lock-1.svg?raw";
import LogoutRaw from "@dashboard/assets/images/icons/streamline/logout-1.svg?raw";
import PuzzleRaw from "@dashboard/assets/images/icons/streamline/module-puzzle-2.svg?raw";
import UnfoldRaw from "@dashboard/assets/images/icons/streamline/move-expand-vertical.svg?raw";
import PeopleRaw from "@dashboard/assets/images/icons/streamline/multiple-neutral-2.svg?raw";
import MoreRaw from "@dashboard/assets/images/icons/streamline/navigation-menu-horizontal.svg?raw";
import MenuRaw from "@dashboard/assets/images/icons/streamline/navigation-menu.svg?raw";
import PinRaw from "@dashboard/assets/images/icons/streamline/pin-location-2.svg?raw";
import QrCodeRaw from "@dashboard/assets/images/icons/streamline/qr-code-scan.svg?raw";
import HelpRaw from "@dashboard/assets/images/icons/streamline/question-circle.svg?raw";
import StarFilledRaw from "@dashboard/assets/images/icons/streamline/rating-star-solid.svg?raw";
import StarRaw from "@dashboard/assets/images/icons/streamline/rating-star.svg?raw";
import RestaurantRaw from "@dashboard/assets/images/icons/streamline/restaurant-fork-knife.svg?raw";
import MayContainRaw from "@dashboard/assets/images/icons/streamline/road-sign-banned-dashed.svg?raw";
import NotAllowedRaw from "@dashboard/assets/images/icons/streamline/road-sign-banned.svg?raw";
import SearchRaw from "@dashboard/assets/images/icons/streamline/search-alternate.svg?raw";
import DragHandleRaw from "@dashboard/assets/images/icons/streamline/select-drag-reorder-dots.svg?raw";
import ExternalLinkRaw from "@dashboard/assets/images/icons/streamline/share-external-link-1.svg?raw";
import ShopRaw from "@dashboard/assets/images/icons/streamline/shop.svg?raw";
import PreferencesRaw from "@dashboard/assets/images/icons/streamline/slider-horizontal-alternative.svg?raw";
import RefreshRaw from "@dashboard/assets/images/icons/streamline/synchronize-refresh-arrow-2.svg?raw";
import ClockRaw from "@dashboard/assets/images/icons/streamline/time-clock-circle.svg?raw";
import EyeRaw from "@dashboard/assets/images/icons/streamline/view-1.svg?raw";

import { StreamlineIcon } from "./StreamlineIcon";

/**
 * The plain marks, named for what they show. Anything that carries a verb and
 * behaviour of its own lives in actionIcons instead.
 *
 * Each is added as a page stops using its MUI counterpart, so the list is what
 * the dashboard draws rather than everything the set contains.
 */
type IconProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "dangerouslySetInnerHTML" | "children"
> & { size?: number; sx?: SxProps<Theme> };

// Takes a ref and passes everything else on, so a mark can carry a click or a
// tooltip's handlers the same as the component underneath it. The ref is the
// part that has to be deliberate: a tooltip hands one to whatever it wraps, and
// a plain function component drops it and never opens.
const icon = (svg: string) => {
  const Mark = React.forwardRef<HTMLSpanElement, IconProps>((props, ref) => (
    <StreamlineIcon svg={svg} ref={ref} {...props} />
  ));
  Mark.displayName = "StreamlineMark";
  return Mark;
};

export const AddCircleIcon = icon(AddCircleRaw);
export const AddIcon = icon(AddRaw);
export const ArrowRightIcon = icon(ArrowRightRaw);
export const BackIcon = icon(BackRaw);
/** A state, not a control, so it is filled where the rest are drawn. */
export const ApprovedIcon = icon(ApprovedRaw);
/** Turns a row open in place. The ringed DropdownIcon is for a control that
 * opens a list somewhere else. */
export const ChevronDownIcon = icon(ChevronDownRaw);
export const ChevronLeftIcon = icon(ChevronLeftRaw);
export const ChevronRightIcon = icon(ChevronRightRaw);
export const CameraIcon = icon(CameraRaw);
export const CheckIcon = icon(CheckRaw);
export const CloseIcon = icon(CloseRaw);
export const RestaurantIcon = icon(RestaurantRaw);
export const CodeIcon = icon(AngleBracketsRaw);
export const DownloadIcon = icon(DownloadRaw);
/** Says a control opens a list. The ring and the chevron carry the same
 * stroke as every other mark, so it sits in the family. */
export const DropdownIcon = icon(DropdownRaw);
export const DuplicateIcon = icon(DuplicateRaw);
export const DragHandleIcon = icon(DragHandleRaw);
export const EmailIcon = icon(EmailRaw);
export const EyeIcon = icon(EyeRaw);
export const EyeOffIcon = icon(EyeOffRaw);
export const FilterIcon = icon(FilterRaw);
export const FolderIcon = icon(FolderRaw);
export const GoogleIcon = icon(GoogleRaw);
export const HelpIcon = icon(HelpRaw);
/** Stands in for a picture that cannot be shown, not for a picture. */
export const ImageFileIcon = icon(ImageFileRaw);
export const LinkIcon = icon(LinkRaw);
export const UnlinkIcon = icon(UnlinkRaw);
/**
 * A badge, not a tick: says the record itself is trusted. Filled, because it
 * reports a state rather than offering an action, the same as ApprovedIcon.
 */
export const VerifiedIcon = icon(VerifiedRaw);
export const ExternalLinkIcon = icon(ExternalLinkRaw);
/** Filled, and small: it marks a hint, it is not the thing being read. */
/**
 * Explains the thing beside it. Carries the help cursor, since hovering is the
 * whole interaction: an arrow says nothing is here and a pointer would promise
 * a click that never comes.
 */
const InfoCircleMark = icon(InfoCircleRaw);
export const InfoCircleIcon = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<typeof InfoCircleMark>
>(({ sx, ...props }, ref) => (
  <InfoCircleMark
    ref={ref}
    sx={[{ cursor: "help" }, ...(Array.isArray(sx) ? sx : [sx])]}
    {...props}
  />
));
InfoCircleIcon.displayName = "InfoCircleIcon";
export const LockIcon = icon(LockRaw);
export const MenuIcon = icon(MenuRaw);
/** Three dots. Opens the actions that did not fit on the row. */
export const MoreIcon = icon(MoreRaw);
/** The off half of a toggle whose on half is a tick. */
export const QrCodeIcon = icon(QrCodeRaw);
export const RefreshIcon = icon(RefreshRaw);
export const SearchIcon = icon(SearchRaw);
export const WarningIcon = icon(AlertCircleRaw);
/** The louder half of the pair. Filled, so severity does not rest on
 * colour alone where a warning sits in the same slot. */
export const DangerIcon = icon(DangerRaw);

export const AnalyticsIcon = icon(AnalyticsRaw);
export const CreditCardIcon = icon(CreditCardRaw);
export const LogoutIcon = icon(LogoutRaw);
export const PaletteIcon = icon(PaletteRaw);
export const PeopleIcon = icon(PeopleRaw);
export const PinIcon = icon(PinRaw);
/** Says a thing cannot be had, not that it can be taken out. */
export const NotAllowedIcon = icon(NotAllowedRaw);
export const FlashIcon = icon(FlashRaw);
export const FlashOffIcon = icon(FlashOffRaw);
export const CalendarIcon = icon(CalendarRaw);
export const StarIcon = icon(StarRaw);
/** Solid twin of [[StarIcon]], for the on state. */
export const StarFilledIcon = icon(StarFilledRaw);
export const ClockIcon = icon(ClockRaw);
/** An arrow that goes out and comes back: reuse a value already on screen. */
export const ReturnIcon = icon(ReturnRaw);
/** The allergen mark with a broken ring: same thing, not certain. */
export const MayContainIcon = icon(MayContainRaw);
export const PreferencesIcon = icon(PreferencesRaw);
export const PuzzleIcon = icon(PuzzleRaw);
export const ShopIcon = icon(ShopRaw);
/** Two ways at once: the control switches rather than only opening. */
export const UnfoldIcon = icon(UnfoldRaw);

// Checkbox and radio, as sets. Each is drawn, including the empty state, so a
// row of them lines up whether or not anything is ticked.
export const RadioIcon = icon(RadioRaw);
export const RadioCheckedIcon = icon(RadioCheckedRaw);
